import { selectLoginCompany } from "services/utils/loginAccess";
import { notifySetupProgress } from "components/SetupFlow/progress";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Mui from "@mui/material";
import { userManagementError } from "services/constants/ErrorMessages";
import { themes } from "services/constants";
import { showToast } from "services/utils/Status";
import { productRequestOrder } from "services/utils/companyAccess";
import { useForm } from "react-hook-form";
import {
  AddBoxOutlined,
  Apartment,
  Close,
  CloseFullscreen,
  Delete,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  setCompanyList,
  setCompanyDetails,
  setGlobalSearchValue,
  setProductAccess,
  setTopbarCompanyName,
  setTopbarCompanyId,
} from "services/Redux/userToken";
import sessionHandling from "services/utils/notificationUtils";
import { customGetAPIWithToken } from "services/utils/CustomAPI";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  panelDrawerSx,
  PANEL_DRAWER_WIDTH,
  FORM_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel as AdminFieldLabel,
  GlobalNoticeBanner,
  globalTextFieldSx,
  PAIRED_LABEL_MIN_HEIGHT,
  useExpandedContentLeft,
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

const COMPANIES_PATH = "/admin/administration/companies";

// Label + helper-text pair used above every input in this panel — mirrors
// CreateUser's FieldLabel so the two "create a thing" admin panels share the
// same look (static caption under the label) instead of this screen's old
// hover-only Info-icon tooltip, which hid the guidance until a user happened
// to hover the tiny icon. `minHeight` (see PAIRED_LABEL_MIN_HEIGHT) is
// opt-in, passed by the expanded grid's paired fields so both inputs in a
// row start at the same Y position — forwarded straight through to
// GlobalFieldLabel, which does the actual reservation.
const FieldLabel = ({ children, tooltip, minHeight }) => (
  <AdminFieldLabel helperText={tooltip} minHeight={minHeight}>
    {children}
  </AdminFieldLabel>
);

// Matches CreateUser's userTypeRadio styling — the app's teal brand color on
// check instead of MUI's default blue, which didn't match the rest of the
// GUI here.
const gstCheckboxSx = {
  color: "#9CA3AF",
  "&.Mui-checked": {
    color: designColors.tealDark,
  },
  "&:hover": {
    backgroundColor: "rgba(15, 118, 110, 0.08)",
  },
};

export const AddCompany = ({
  screenName,
  editEompanyDetails: editCompanyDetails,
  setAddCompanyScreen,
  updateData,
  setUpdateData,
  companyDetails,
  setGetCompanyDetails,
  setCompanyApi,
  onClose,
  onCreated,
  open = true,
  expanded = false,
  onToggleExpand,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    trigger,
  } = useForm({
    mode: "onBlur",
  });
  const {
    domain,
    token,
    topbarCompanyId,
    userDetails,
    // Aliased -- the `companyDetails` prop above is this screen's admin
    // table rows; this is the separate redux-cached full company list the
    // Sidebar's logo/name reads from.
    companyDetails: cachedCompanyDetails,
  } = useSelector((state) => state.tokenReducer);
  const [yesCheck, setYesCheck] = useState(false);
  const [noCheck, setNoCheck] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentLeft = useExpandedContentLeft(expanded);
  const isEditMode = screenName === "Edit Company Details";
  const isFirstCompany = companyDetails.length === 0;

  const closePanel = useCallback(() => {
    clearErrors();
    onClose?.();
    setAddCompanyScreen?.(false);
  }, [clearErrors, onClose, setAddCompanyScreen]);

  // Expanded mode is a fixed-position overlay covering the page, but nothing
  // stopped the page underneath from also being scrollable — the overlay's
  // own scroll container plus the still-live background page produced two
  // independent vertical scrollbars at once. Lock the background while
  // expanded, same fix already applied to UserDetailPanel/CompanyDetailPanel.
  useEffect(() => {
    if (!expanded) return;
    if (typeof document === "undefined") return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [expanded]);

  const handleCompanySaved = useCallback(() => {
    setGetCompanyDetails(true);
    setCompanyApi(true);
    onCreated?.();
    closePanel();
  }, [closePanel, onCreated, setCompanyApi, setGetCompanyDetails]);

  useEffect(() => {
    if (updateData) {
      setValue("companyname", editCompanyDetails.companyname);
      setValue("companycode", editCompanyDetails.companycode);
      setValue("gstno", editCompanyDetails.gstno);
      setValue("address", editCompanyDetails.address);
      setValue("email", editCompanyDetails.email);
      setValue("phoneno", editCompanyDetails.phonenumber);
      setUpdateData(false);
      if (editCompanyDetails.gstno !== "" && editCompanyDetails.gstno) {
        setYesCheck(true);
        setNoCheck(false);
      } else {
        setYesCheck(false);
        setNoCheck(true);
      }
    }
    if (
      editCompanyDetails.company_logo_url &&
      editCompanyDetails.company_logo_url.trim() !== ""
    ) {
      setLogoPreview(editCompanyDetails.company_logo_url);
    } else {
      setLogoPreview("");
    }
  }, [
    editCompanyDetails.address,
    editCompanyDetails.company_logo_url,
    editCompanyDetails.companycode,
    editCompanyDetails.companyname,
    editCompanyDetails.email,
    editCompanyDetails.gstno,
    editCompanyDetails.phonenumber,
    setUpdateData,
    setValue,
    updateData,
  ]);

  const refreshCompanyAccess = async () => {
    const companyAccess = await customGetAPIWithToken(
      `${domain}user-group/user/company/`,
      token,
    );

    if (companyAccess.ok) {
      dispatch(setCompanyList(companyAccess.responseDetails));
      localStorage.setItem(
        "companyAccess",
        JSON.stringify(companyAccess.responseDetails),
      );
    }

    return companyAccess;
  };

  const refreshProductAccess = async (companyAccess) => {
    const company = selectLoginCompany(companyAccess.responseDetails || [], userDetails?.type, topbarCompanyId);
    const companyId = company?.company_id ?? null;
    dispatch(setTopbarCompanyId(companyId));
    dispatch(setTopbarCompanyName(company?.company_name || ""));
    if (!company) {
      localStorage.removeItem("topbarCompanyId");
      localStorage.removeItem("topbarCompanyName");
      localStorage.setItem("productAccess", "[]");
      dispatch(setProductAccess([]));
      return;
    }
    localStorage.setItem("topbarCompanyId", companyId);
    localStorage.setItem("topbarCompanyName", company.company_name);
    const request = productRequestOrder.start();
    const productAccess = await customGetAPIWithToken(
      `${domain}login/available/products/?company_id=${companyId}`,
      token,
    );
    if (productAccess?.ok && productRequestOrder.isCurrent(request)) {
      dispatch(setProductAccess({ products: productAccess.responseDetails, company_id: companyId }));
      localStorage.setItem(
        "productAccess",
        JSON.stringify(productAccess.responseDetails),
      );
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting || buttonLoader) {
      return;
    }

    setIsSubmitting(true);

    if (logoFile && logoFile.size > 1000000) {
      showToast("error", userManagementError.imagesizeError);
      setIsSubmitting(false);
      return;
    }

    setButtonLoader(true);
    try {
      const apiActionMethod = isEditMode ? "PUT" : "POST";
      const apiUrl = isEditMode
        ? `${domain}company/${editCompanyDetails?.id}/`
        : `${domain}company/`;
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      if (logoFile) {
        formData.append("company_logo", logoFile, logoFile.name);
      }
      formData.append("companyname", data.companyname);
      formData.append("company_code", data.companycode);
      formData.append("address", data.address);
      formData.append("gstnumber", data.gstno);
      formData.append("email", data.email);
      formData.append("isactive", true);
      formData.append("phonenumber", data.phoneno);
      formData.append("isprimary", editCompanyDetails.primary);

      const response = await fetch(apiUrl, {
        method: apiActionMethod,
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      });
      const responseData = await response.json();

      if (response.ok) {
        const companyAccess = await refreshCompanyAccess();
        if (topbarCompanyId === editCompanyDetails?.id) {
          dispatch(setTopbarCompanyName(data.companyname));
        }

        // Keep the cache the Sidebar's logo/company-name reads from in sync
        // -- it's populated once at login and otherwise only refreshed on a
        // hard reload, so without this a newly created/updated company (and
        // any logo on it) wouldn't show up there until the user logged out
        // and back in.
        if (responseData?.id != null) {
          const existing = cachedCompanyDetails ?? [];
          const patchedCompanyDetails = isEditMode
            ? existing.map((item) =>
                item.id === responseData.id ? { ...item, ...responseData } : item,
              )
            : [...existing, responseData];
          dispatch(setCompanyDetails(patchedCompanyDetails));
          localStorage.setItem(
            "companyDetails",
            JSON.stringify({ results: patchedCompanyDetails }),
          );
        }

        if (!isEditMode) notifySetupProgress("company");
        if (isFirstCompany) {
          showToast("success", "Company created.");
          if (companyAccess.ok) {
            await refreshProductAccess(companyAccess);
          }
          reset();
          dispatch(setGlobalSearchValue(""));
          // Stay on (or return to) the Companies list so the new company is
          // the first thing the user sees.
          handleCompanySaved();
          if (!history.location.pathname.startsWith(COMPANIES_PATH)) {
            history.push(COMPANIES_PATH);
          }
        } else {
          showToast(
            "success",
            isEditMode
              ? "Company updated successfully"
              : "Company created successfully",
          );
          reset();
          handleCompanySaved();
        }
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Addcompany] operation failed:", error);
      showToast(
        "error",
        "Couldn't save the company details. Please try again.",
      );
    } finally {
      setButtonLoader(false);
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    const charCode = e.charCode;
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  const setYesCheckUpdated = () => {
    if (yesCheck) {
      setNoCheck(true);
      setYesCheck(false);
      setValue("gstno", "");
    } else {
      setYesCheck(true);
      setNoCheck(false);
    }
  };

  const setNoCheckUpdated = () => {
    if (noCheck) {
      setNoCheck(false);
      setYesCheck(true);
    } else {
      setNoCheck(true);
      setYesCheck(false);
      setValue("gstno", "");
    }
  };

  const handleKeyDown = (e) => {
    const allowedKeys = /^[A-Za-z\s 0-9]$/;
    if (!allowedKeys.test(e.key)) {
      e.preventDefault();
    }
  };

  const checkSpecialChar = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const removeCompany = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      formData.append("isdeleted", true);
      const response = await fetch(
        `${domain}company/${editCompanyDetails?.id}/`,
        {
          method: "PUT",
          headers: myHeaders,
          body: formData,
          redirect: "follow",
        },
      );
      await response.json();
      if (response.ok) {
        setGetCompanyDetails(true);
        onCreated?.();
        closePanel();
        showToast("success", "Company updated.");
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Addcompany] operation failed:", error);
      showToast(
        "error",
        "Couldn't save the company details. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateGST = (gstNo) => {
    const regex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return regex.test(gstNo);
  };

  const checkExistingCompanyName = useCallback(
    async (companyName) => {
      try {
        const response = await fetch(
          `${domain}check_company_name/?company_name=${companyName}`,
          {
            method: "GET",
            headers: {
              "content-type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          if (res?.exists) {
            return "That company name is already taken.Please choose a unique name.";
          }
          return true;
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Addcompany] operation failed:", error);
        showToast(
          "error",
          "Couldn't save the company details. Please try again.",
        );
      }
    },
    [domain, token],
  );

  const checkExistingCompanyCode = useCallback(
    async (companyCode) => {
      try {
        const response = await fetch(
          `${domain}check_company_name/?company_code=${companyCode}`,
          {
            method: "GET",
            headers: {
              "content-type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          if (res?.exists) {
            return "That company code is already taken.Please choose a unique code.";
          }
          return true;
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Addcompany] operation failed:", error);
        showToast(
          "error",
          "Couldn't save the company details. Please try again.",
        );
      }
    },
    [domain, token],
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      if (img.width > 200 || img.height > 45) {
        showToast(
          "error",
          "Logo should not exceed 200px width or 45px height.",
        );
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    };
    img.src = URL.createObjectURL(file);
  };

  const panelTitle = isEditMode ? "Edit Company" : "Create Company";
  const panelSubtitle = isEditMode
    ? "Update the company profile, access details, and branding."
    : "Create a new company profile for this tenant.";

  // Each field as its own named node — shared by both the expanded full-page
  // card and the collapsed drawer panel below, which wrap it very
  // differently (one in a bordered/shadowed card, the other flat on the
  // panel background) but must never drift apart on the actual
  // inputs/validation. Kept as separate nodes (rather than one fixed Stack)
  // so expanded mode can lay related fields out as side-by-side pairs —
  // matches CreateUser's expanded layout — instead of running every field
  // one-per-row down a mostly-empty full-width page.
  const isFirstCompanyBanner = isFirstCompany ? (
    <GlobalNoticeBanner icon={<Apartment fontSize="small" />} sx={{ mb: 3 }}>
      At least one company is required. Complete this form to create the first
      company in the workspace.
    </GlobalNoticeBanner>
  ) : null;

  const companyNameField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Update the legal or display name of the company as required."
            : "Enter the official/legal name of your company."
        }
      >
        Company Name
      </FieldLabel>
      <Mui.TextField
        {...register("companyname", {
          required: "Company name is required",
          maxLength: {
            value: 32,
            message: "Maximum length is 32 characters",
          },
          minLength: {
            value: 3,
            message: "Minimum length is 3 characters",
          },
          validate: {
            companyName: async (fieldValue) => {
              if (!/[A-Za-z]/.test(fieldValue)) {
                return "Company name must contain at least one alphabet character.";
              } else if (/^\d+$/.test(fieldValue)) {
                return "Company name should not contain digits.";
              } else if (fieldValue !== "" && screenName === "Create Company") {
                return await checkExistingCompanyName(fieldValue.trim());
              } else if (
                fieldValue !== "" &&
                fieldValue !== editCompanyDetails.companyname &&
                isEditMode
              ) {
                return await checkExistingCompanyName(fieldValue.trim());
              }
              return true;
            },
          },
        })}
        error={errors.companyname ? true : false}
        helperText={errors.companyname?.message}
        sx={globalTextFieldSx}
        onKeyPress={handleKeyDown}
        name="companyname"
        placeholder="Enter company name"
        size="small"
        fullWidth
      />
    </Mui.Box>
  );

  const companyCodeField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Modify the unique identifier for the company if needed.(Note: Ensure this remains unique to avoid conflicts.)"
            : "Provide a unique code to identify the company within the system.Example: ABC001"
        }
      >
        Company Code
      </FieldLabel>
      <Mui.TextField
        {...register("companycode", {
          required: "Company code is required",
          minLength: {
            value: 3,
            message: "At least 3 characters required",
          },
          validate: {
            companyCode: async (fieldValue) => {
              const cleanedValue = fieldValue.replace(/[a-zA-Z^-]/g, "");
              setValue("companycode", cleanedValue);

              if (cleanedValue !== "" && screenName === "Create Company") {
                return await checkExistingCompanyCode(cleanedValue.trim());
              } else if (
                cleanedValue !== "" &&
                cleanedValue !== editCompanyDetails.companycode &&
                isEditMode
              ) {
                return await checkExistingCompanyCode(cleanedValue.trim());
              }
              return true;
            },
          },
        })}
        error={errors.companycode ? true : false}
        helperText={errors.companycode?.message}
        sx={globalTextFieldSx}
        name="companycode"
        placeholder="Enter company code"
        onKeyPress={checkSpecialChar}
        InputProps={{
          inputProps: { maxLength: 3 },
        }}
        fullWidth
        size="small"
        onBlur={async () => {
          await trigger("companycode");
        }}
      />
    </Mui.Box>
  );

  const gstField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Toggle between Yes or No to update the GST registration status of the company.Changing this may affect tax-related processes."
            : "Select whether the company is registered under GST by choosing Yes or No.This will impact tax calculations and compliance."
        }
      >
        Is GST Registered
      </FieldLabel>
      <Mui.Stack direction="row" spacing={1} alignItems="center">
        <Mui.Checkbox
          checked={yesCheck}
          onChange={() => setYesCheckUpdated()}
          sx={gstCheckboxSx}
        />
        <Mui.Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          Yes
        </Mui.Typography>
        <Mui.Checkbox
          checked={noCheck}
          onChange={() => setNoCheckUpdated()}
          sx={gstCheckboxSx}
        />
        <Mui.Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          No
        </Mui.Typography>
      </Mui.Stack>
      {yesCheck ? (
        <Mui.TextField
          {...register("gstno", {
            required: yesCheck ? "Gstno is required" : false,
            validate: {
              gstNoValidation: async (fieldValue) => {
                if (fieldValue !== "" && !noCheck) {
                  const valid = validateGST(fieldValue);
                  return valid ? true : "GSTNO in format 22AAAAA0000A1Z5";
                }
              },
            },
          })}
          error={errors.gstno ? true : false}
          helperText={errors.gstno?.message}
          sx={globalTextFieldSx}
          name="gstno"
          placeholder="GSTNO in format 22AAAAA0000A1Z5"
          onKeyPress={handleKeyDown}
          disabled={!yesCheck}
          InputProps={{
            inputProps: { maxLength: 15 },
          }}
          size="small"
          fullWidth
        />
      ) : null}
    </Mui.Box>
  );

  const emailField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Update the official company email address used for notifications and reports."
            : "Enter the official email ID of the company for communication and report delivery."
        }
      >
        Email
      </FieldLabel>
      <Mui.TextField
        id="email"
        variant="outlined"
        {...register("email", {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid e-mail address",
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email?.message || ""}
        placeholder="Enter email"
        size="small"
        fullWidth
        sx={globalTextFieldSx}
      />
    </Mui.Box>
  );

  const phoneField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Edit the company's contact number for support, alerts, or internal communication."
            : "Input a valid contact number that can be used for support and verification."
        }
      >
        Phone Number
      </FieldLabel>
      <Mui.TextField
        {...register("phoneno", {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Minimum length is 10 digits",
          },
          maxLength: {
            value: 15,
            message: "Maximum length is 15 digits",
          },
          validate: {
            formatCheck: (value) => {
              const cleanedValue = value.replace(/[^\d-]/g, "");
              setValue("phoneno", cleanedValue);
              return true;
            },
          },
        })}
        error={errors.phoneno ? true : false}
        helperText={errors.phoneno?.message}
        sx={globalTextFieldSx}
        name="phoneno"
        onBlur={async () => {
          await trigger("phoneno");
        }}
        placeholder="Enter phone number"
        onKeyPress={handleKeyPress}
        fullWidth
        size="small"
        InputProps={{
          inputProps: { maxLength: 15 },
        }}
      />
    </Mui.Box>
  );

  const addressField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip={
          isEditMode
            ? "Modify the company's main office or billing address as necessary."
            : "Type the full postal address of the company's main office or headquarters."
        }
      >
        Address
      </FieldLabel>
      <Mui.TextField
        multiline
        rows={2}
        maxRows={5}
        {...register("address", {
          required: "Address is required",
        })}
        error={errors.address ? true : false}
        helperText={errors.address?.message}
        sx={globalTextFieldSx}
        name="address"
        placeholder="Enter address"
        size="small"
        fullWidth
      />
      {isFirstCompany ? (
        <Mui.Stack sx={{ paddingTop: 2 }}>
          <Mui.Typography>
            <label style={{ color: themes.redColor, fontSize: 25 }}>*</label>
            Additional companies can be created under admin page
          </Mui.Typography>
        </Mui.Stack>
      ) : null}
    </Mui.Box>
  );

  const logoOrDeleteField = editCompanyDetails.isactive ? (
    <Mui.Box>
      <FieldLabel
        tooltip={
          isEditMode
            ? "You can change or remove the existing logo. Upload a new one if needed."
            : "(Optional) Upload the company logo. It will appear on payslips and reports."
        }
      >
        Company Logo
      </FieldLabel>
      <Mui.Button
        variant="outlined"
        component="label"
        sx={{
          alignItems: "center",
          textTransform: "capitalize",
          border: `1px solid ${designColors.slate300}`,
          borderRadius: designRadii.control,
          color: designColors.slate700,
          backgroundColor: designColors.white,
          "&:hover": {
            borderColor: designColors.slate400,
            backgroundColor: designColors.slateBg,
          },
        }}
        size="small"
        fullWidth
        disabled={isSubmitting}
      >
        <Mui.Stack>
          <AddBoxOutlined
            sx={{
              marginLeft: 3,
              fontSize: 25,
              color: designColors.slate700,
            }}
          />
          <Mui.Typography
            fontSize={18}
            sx={{
              textTransform: "none",
              color: designColors.slate700,
            }}
          >
            Add Logo
          </Mui.Typography>
        </Mui.Stack>
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={handleLogoChange}
          disabled={isSubmitting}
        />
      </Mui.Button>
      <Mui.Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          textAlign: "center",
          color: designColors.slate600,
        }}
      >
        Recommended size: 200 x 45 px
      </Mui.Typography>

      <Mui.Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        {logoFile ? (
          <img
            src={URL.createObjectURL(logoFile)}
            alt="Uploaded Logo"
            style={{ maxWidth: "200px", maxHeight: "45px" }}
          />
        ) : logoPreview ? (
          <img
            src={logoPreview}
            alt="Existing Logo"
            style={{ maxWidth: "200px", maxHeight: "45px" }}
          />
        ) : (
          <Mui.Typography
            sx={{
              width: "200px",
              height: "45px",
              border: `1px solid ${designColors.slate300}`,
              borderRadius: designRadii.control,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {editCompanyDetails?.companyname || "No Logo"}
          </Mui.Typography>
        )}
      </Mui.Box>
    </Mui.Box>
  ) : (
    <Mui.Box
      sx={{
        border: "1px solid #FCA5A5",
        borderRadius: designRadii.card,
        backgroundColor: "#FEF2F2",
        padding: 2,
      }}
    >
      <Mui.Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: designColors.slate900,
          paddingBottom: 1,
        }}
      >
        Delete Company
      </Mui.Typography>
      <Mui.Typography
        sx={{
          fontSize: 14,
          color: designColors.slate700,
          paddingBottom: 1.5,
        }}
      >
        Deleting the company will permanently remove all data and revoke access
        for all associated users.
      </Mui.Typography>
      <Mui.Stack direction="row" justifyContent="flex-end">
        <Mui.Button
          onClick={() => removeCompany()}
          disabled={isSubmitting}
          startIcon={<Delete fontSize="small" />}
          sx={{
            minHeight: 42,
            px: 2,
            borderRadius: designRadii.control,
            textTransform: "none",
            fontWeight: 600,
            color: "#B42318",
            border: "1px solid #FCA5A5",
            backgroundColor: designColors.white,
            "&:hover": {
              backgroundColor: "#FEF2F2",
            },
          }}
        >
          Delete Company
        </Mui.Button>
      </Mui.Stack>
    </Mui.Box>
  );

  // Expanded mode has the width to spare, so short related fields pair up
  // into a 2-column grid instead of each taking its own full-width row.
  const expandedFieldsLayout = (
    <Mui.Grid container spacing={3}>
      <Mui.Grid item xs={12} sm={6}>
        {companyNameField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {companyCodeField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {gstField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {emailField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {phoneField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {addressField}
      </Mui.Grid>
      <Mui.Grid item xs={12}>
        {logoOrDeleteField}
      </Mui.Grid>
    </Mui.Grid>
  );

  // The narrow collapsed drawer keeps a single stacked column — no room for
  // side-by-side pairs there.
  const collapsedFieldsLayout = (
    <Mui.Stack spacing={2.5}>
      {companyNameField}
      {companyCodeField}
      {gstField}
      {emailField}
      {phoneField}
      {addressField}
      {logoOrDeleteField}
    </Mui.Stack>
  );

  const formFieldsBody = (
    <>
      {isFirstCompanyBanner}
      {expanded ? expandedFieldsLayout : collapsedFieldsLayout}
    </>
  );

  const formActions = (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={closePanel}>Cancel</SecondaryButton>
      {editCompanyDetails.isactive ? (
        <Mui.Button
          type="submit"
          disabled={isSubmitting || buttonLoader}
          aria-busy={buttonLoader}
          startIcon={
            buttonLoader ? (
              <Mui.CircularProgress size={16} thickness={5} color="inherit" />
            ) : null
          }
          sx={{
            ...designShell.primaryButtonBase,
            minWidth: FORM_ACTION_BUTTON_WIDTH,
            // Keep the teal fill while saving; only the spinner and label
            // show that the request is running.
            ...(buttonLoader && {
              "&.Mui-disabled": {
                background: designShell.primaryButtonBase.background,
                color: designShell.primaryButtonBase.color,
                opacity: 0.85,
              },
            }),
          }}
        >
          {buttonLoader
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
              ? "Update"
              : "Create"}
        </Mui.Button>
      ) : null}
    </Mui.Stack>
  );

  // One seamless panel (no border/shadow, just white-on-pageBg contrast) —
  // matches CompanyDetailPanel's expanded layout exactly: breadcrumb+title
  // header, a single scrollable fields region, and a footer pinned to the
  // bottom, all inside one rounded box instead of two separate bordered
  // cards with a gap between them.
  const panelContent = (
    <Mui.Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        backgroundColor: designColors.white,
        borderRadius: designRadii.card,
      }}
      role="presentation"
    >
      <Mui.Box sx={{ p: 3, pb: 0 }}>
        <GlobalBreadcrumb
          trail={[
            "Administration",
            { label: "Companies", onClick: closePanel },
            panelTitle,
          ]}
        />
        <Mui.Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{
            pb: 2.25,
            borderBottom: `1px solid ${designColors.slate200}`,
          }}
        >
          <Mui.Box>
            <Mui.Typography sx={designType.pageTitle}>
              {panelTitle}
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              {panelSubtitle}
            </Mui.Typography>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1}>
            {onToggleExpand ? (
              <PanelHeaderIconButton
                expanded
                title="Collapse"
                onClick={onToggleExpand}
              >
                <CloseFullscreen />
              </PanelHeaderIconButton>
            ) : null}
            <PanelHeaderIconButton
              expanded
              title="Close"
              onClick={closePanel}
            >
              <Close />
            </PanelHeaderIconButton>
          </Mui.Stack>
        </Mui.Stack>
      </Mui.Box>

      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Mui.Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 3,
            pt: 2,
            pb: 2,
          }}
        >
          {formFieldsBody}
        </Mui.Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          {formActions}
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
  );

  if (expanded) {
    return createPortal(
      <Mui.Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Mui.Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Mui.Box>
      </Mui.Box>,
      document.body,
    );
  }

  // Rendered as this component's own Drawer (rather than the parent
  // wrapping it externally) so the parent can mount AddCompany once and
  // just flip `open`/`expanded` props — switching between this collapsed
  // Drawer and the expanded portal above no longer unmounts/remounts the
  // component, which used to wipe out whatever the user had typed so far.
  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={closePanel}
      sx={panelDrawerSx()}
    >
      <Mui.Box
        sx={{
          width: PANEL_DRAWER_WIDTH,
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          boxSizing: "border-box",
        }}
        role="presentation"
      >
        <Mui.Box sx={{ p: 3, pb: 0 }}>
          <Mui.Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Mui.Box sx={{ pr: 2 }}>
              <Mui.Typography sx={designType.panelTitle}>
                {panelTitle}
              </Mui.Typography>
              <Mui.Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                {panelSubtitle}
              </Mui.Typography>
            </Mui.Box>
            <Mui.Stack direction="row" spacing={1}>
              {onToggleExpand ? (
                <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                  <Mui.IconButton
                    onClick={onToggleExpand}
                    size="small"
                    sx={{ ...designShell.panelSoftIconButton }}
                  >
                    <OpenFullViewIcon fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              ) : null}
              <Mui.IconButton
                onClick={closePanel}
                size="small"
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <Close fontSize="small" />
              </Mui.IconButton>
            </Mui.Stack>
          </Mui.Stack>

          <Mui.Box
            sx={{ mt: 2, borderBottom: `1px solid ${designColors.slate200}` }}
          />
        </Mui.Box>

        <Mui.Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Mui.Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pt: 2.5,
              px: 3,
              pb: 2,
            }}
          >
            {formFieldsBody}
          </Mui.Box>
          <Mui.Box
            sx={{
              px: 3,
              py: 1.25,
              borderTop: "1px solid #E2E8F0",
              backgroundColor: designColors.white,
            }}
          >
            {formActions}
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>
    </Mui.Drawer>
  );
};
